"use client";

import React, {useEffect, useState, useRef} from "react";
import {motion, useScroll, useTransform} from "framer-motion";

import Image from "next/image";
import DecorationLine from "@/components/global/ui/DecorationLine";
import Chip from "@/components/global/ui/Chip";
import {Carousel, type CarouselApi, CarouselContent, CarouselItem} from "@/shadcn/ui/carousel";
import {cn} from "@/lib/utils";
import {PortableText} from "@portabletext/react";
import {richTextComponent} from "@/sanity/sanityPortableText";
import ScrollingBanner from "@/components/global/ui/ScrollingBanner";
import {CTACornerButton} from "@/shadcn/ui/corner-button";

import {PageHomeNewSchema} from "@/sanity/pages/PageHomeNewTemplate";

import pixelOverlay from "@/public/home-new/pixel-overlay.svg";

export default function SolutionsSection({
  data,
  className
}: {
  data: PageHomeNewSchema;
  className?: string;
}) {
  const [api, setApi] = useState<CarouselApi>();
  const [current, setCurrent] = useState(0);
  const [canPrev, setCanPrev] = useState(false);
  const [canNext, setCanNext] = useState(false);

  const parallaxRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: parallaxRef,
    offset: ["start end", "end start"]
  });
  const y = useTransform(scrollYProgress, [0, 1], ["-22%", "22%"]);

  useEffect(() => {
    if (!api) return;

    const onSelect = () => {
      setCurrent(api.selectedScrollSnap());
      setCanPrev(api.canScrollPrev());
      setCanNext(api.canScrollNext());
    };

    onSelect();
    api.on("select", onSelect);
    api.on("reInit", onSelect);
    return () => {
      api.off("select", onSelect);
      api.off("reInit", onSelect);
    };
  }, [api]);

  return (
    <section className={cn(
      "relative overflow-clip",
      className
    )}>
      <div className="relative overflow-clip py-[12rem] lg:py-[18rem]">
        <DecorationLine
          className="top-0"
          orientation="vertical"
          position="left"
        />
        <DecorationLine
          className="top-0"
          orientation="vertical"
          position="right"
        />

        <div className="relative pb-[3rem] lg:pb-[5rem]">
          <DecorationLine
            className="bottom-0 left-0"
            orientation="horizontal"
          />

          <div className="container">
            <div className="mx-auto w-[min(187.75rem,100%)] flex flex-col lg:flex-row items-center lg:items-end lg:justify-between">
              <div className="flex flex-col items-center lg:items-start">
                <Chip
                  image={data?.solutionsChip?.icon}
                  text={data?.solutionsChip?.text}
                />

                {data?.solutionsTitle && (
                  <div className="richtext lg:richtext-start prose-p:text-[2rem] prose-p:w-[min(92.5rem,100%)] prose-headings:w-[min(82rem,100%)]">
                    <PortableText
                      value={data.solutionsTitle}
                      components={richTextComponent}
                    />
                  </div>
                )}

                {data?.letterCTA && (
                  <CTACornerButton
                    className="mt-[3rem]"
                    data={data.letterCTA}
                  />
                )}
              </div>

              {data?.solutionsItems && (
                <div className="hidden lg:flex items-end justify-end flex-shrink-0">
                  <div className={cn(
                    "w-full flex gap-[1rem] px-[1.25rem] py-[1.125rem] relative border-[2px] border-white rounded-full",
                    "after:absolute after:-top-[1px] after:-left-[1px] after:-right-[1px] after:-bottom-[1px] after:border after:border-[#D3D3D2] after:rounded-full after:pointer-events-none"
                  )}>
                    <button
                      onClick={() => api?.scrollPrev()}
                      className="w-[6rem] h-[6rem] flex-shrink-0 rounded-full flex items-center justify-center bg-[#202223] transition-all text-white disabled:bg-[#202223]/10 disabled:text-[#202223] disabled:cursor-not-allowed"
                      disabled={!api || !canPrev}
                      aria-label="Previous slide"
                    >
                      <svg viewBox="0 0 12 20" xmlns="http://www.w3.org/2000/svg" className="w-[1.25rem] h-auto flex-shrink-0 -translate-x-[.125rem]">
                        <path d="M2.47955e-05 10L10 20L11.4 18.6L2.80002 10L11.4 1.4L10 0L2.47955e-05 10Z" fill="currentColor"/>
                      </svg>
                    </button>

                    <button
                      onClick={() => api?.scrollNext()}
                      className="w-[6rem] h-[6rem] flex-shrink-0 rounded-full flex items-center justify-center bg-[#202223] transition-all text-white disabled:bg-[#202223]/10 disabled:text-[#202223] disabled:cursor-not-allowed"
                      disabled={!api || !canNext}
                      aria-label="Next slide"
                    >
                      <svg viewBox="0 0 12 20" xmlns="http://www.w3.org/2000/svg" className="rotate-180 w-[1.25rem] h-auto flex-shrink-0 translate-x-[.125rem]">
                        <path d="M2.47955e-05 10L10 20L11.4 18.6L2.80002 10L11.4 1.4L10 0L2.47955e-05 10Z" fill="currentColor"/>
                      </svg>
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {data?.solutionsItems && (
          <div className="relative overflow-clip">
            <DecorationLine
              className="bottom-0 left-0"
              orientation="horizontal"
            />

            <div className="container flex flex-col items-center py-[3rem] lg:py-[5rem]">
              <div className="w-[min(187.75rem,100%)] flex flex-col items-center">
                <Carousel
                  className="w-full [&_>_div]:!overflow-visible"
                  setApi={setApi}
                  opts={{
                    align: "start",
                    skipSnaps: false,
                  }}
                >
                  <CarouselContent className="w-full gap-x-[2rem]">
                    {data?.solutionsItems?.map((item, index) => (
                      <CarouselItem
                        key={index}
                        className="p-0 basis-[42.5rem] lg:basis-[56rem] flex flex-col items-start"
                      >
                        {item?.image && (
                          <Image
                            className="!w-full mb-[2rem] lg:mb-[2.5rem]"
                            src={item.image}
                            quality={95}
                            alt=""
                          />
                        )}

                        {item?.text && (
                          <div className="richtext richtext-start prose-headings:text-[3rem] prose-headings:mb-[.5rem]">
                            <PortableText
                              value={item.text}
                              components={richTextComponent}
                            />
                          </div>
                        )}
                      </CarouselItem>
                    ))}
                  </CarouselContent>
                </Carousel>

                <div className={cn(
                  "w-full sm:w-auto flex lg:hidden items-center justify-between sm:justify-center gap-[2.25rem] mt-[2.5rem] px-[1.25rem] py-[1.125rem] relative border-[2px] border-white rounded-full",
                  "after:absolute after:-top-[1px] after:-left-[1px] after:-right-[1px] after:-bottom-[1px] after:border after:border-[#D3D3D2] after:rounded-full after:pointer-events-none"
                )}>
                  <button
                    onClick={() => api?.scrollPrev()}
                    className="w-[6rem] h-[6rem] flex-shrink-0 rounded-full flex items-center justify-center bg-[#202223] transition-all text-white disabled:bg-[#202223]/10 disabled:text-[#202223] disabled:cursor-not-allowed"
                    disabled={!api || !canPrev}
                    aria-label="Previous slide"
                  >
                    <svg viewBox="0 0 12 20" xmlns="http://www.w3.org/2000/svg" className="w-[1.25rem] h-auto flex-shrink-0 -translate-x-[.125rem]">
                      <path d="M2.47955e-05 10L10 20L11.4 18.6L2.80002 10L11.4 1.4L10 0L2.47955e-05 10Z" fill="currentColor"/>
                    </svg>
                  </button>

                  <div className="sm:w-[40rem] h-[.5rem] grow sm:grow-0 flex gap-[1rem] px-[.5rem]">
                    {data?.solutionsItems?.map((_, index) => (
                      <div
                        key={index}
                        className={cn(
                          "grow h-full transition-colors",
                          index === current
                            ? "bg-[#202223]"
                            : "bg-[#202223]/20"
                        )}
                      />
                    ))}
                  </div>

                  <button
                    onClick={() => api?.scrollNext()}
                    className="w-[6rem] h-[6rem] flex-shrink-0 rounded-full flex items-center justify-center bg-[#202223] transition-all text-white disabled:bg-[#202223]/10 disabled:text-[#202223] disabled:cursor-not-allowed"
                    disabled={!api || !canNext}
                    aria-label="Next slide"
                  >
                    <svg viewBox="0 0 12 20" xmlns="http://www.w3.org/2000/svg" className="rotate-180 w-[1.25rem] h-auto flex-shrink-0 translate-x-[.125rem]">
                      <path d="M2.47955e-05 10L10 20L11.4 18.6L2.80002 10L11.4 1.4L10 0L2.47955e-05 10Z" fill="currentColor"/>
                    </svg>
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {data?.solutionsImage && (
        <div ref={parallaxRef} className="relative overflow-clip">
          <motion.div style={{ y }}>
            <Image
              className="!w-full aspect-[430/500] lg:aspect-[1920/800] object-cover scale-110"
              src={data.solutionsImage}
              quality={95}
              alt="event image banner"
            />
          </motion.div>

          <Image
            className="absolute left-0 -bottom-[1px] !w-[max(100rem,100%)] !max-w-[unset]"
            src={pixelOverlay}
            alt="event image banner pixel overlay"
          />
        </div>
      )}

      {data?.solutionsScroll &&
        <ScrollingBanner
          text={data.solutionsScroll}
        />
      }
    </section>
  );
}
